import React, { useState, useEffect } from 'react';
import { getDocumentos, createDocumento, deleteDocumento, getComunidades } from '../services/api';
import { useToast } from '../components/Toast';
import Pagination from '../components/Pagination';
import DateInput from '../components/DateInput';
import Button from '../components/ui/Button';
import EmptyState from '../components/EmptyState';
import ConfirmModal from '../components/ConfirmModal';
import './Documentos.css';

function Documentos() {
  const toast = useToast();
  const [documentos, setDocumentos] = useState([]);
  const [documentosFiltrados, setDocumentosFiltrados] = useState([]);
  const [comunidades, setComunidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkComunidadId, setBulkComunidadId] = useState('');
  const [bulkProgress, setBulkProgress] = useState(null); // { current, total, errors }
  const [user, setUser] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filtroComunidad, setFiltroComunidad] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [nuevoDoc, setNuevoDoc] = useState({    nombre: '',
    descripcion: '',
    categoria: 'acta',
    comunidad_id: '',
    archivo: null
  });

  const categorias = {
    acta: { label: 'Acta', color: '#3b82f6', icon: '📋' },
    estatuto: { label: 'Estatuto', color: '#ef4444', icon: '⚖️' },
    presupuesto: { label: 'Presupuesto', color: '#22c55e', icon: '💰' },
    factura: { label: 'Factura', color: '#f59e0b', icon: '📄' },
    contrato: { label: 'Contrato', color: '#a855f7', icon: '📝' },
    certificado: { label: 'Certificado', color: '#f97316', icon: '✅' },
    reglamento: { label: 'Reglamento', color: '#0ea5e9', icon: '📗' },
    informe: { label: 'Informe', color: '#64748b', icon: '📊' },
    circular: { label: 'Circular', color: '#ec4899', icon: '📢' },
    otro: { label: 'Otro', color: '#9ca3af', icon: '📎' }
  };

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
      if (userData.rol === 'administrador') {
        cargarComunidades();
      }
    }
    cargarDocumentos();
  }, []);

  useEffect(() => {
    aplicarFiltros();
  }, [documentos, filtroCategoria, busqueda, fechaDesde, fechaHasta, filtroComunidad]);

  const cargarDocumentos = async () => {
    try {
      const response = await getDocumentos();
      setDocumentos(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    let resultado = [...documentos];

    // Filtro por categoría
    if (filtroCategoria) {
      resultado = resultado.filter(doc => doc.categoria === filtroCategoria);
    }

    // Filtro por búsqueda
    if (busqueda) {
      const busquedaLower = busqueda.toLowerCase();
      resultado = resultado.filter(doc =>
        doc.nombre.toLowerCase().includes(busquedaLower) ||
        (doc.descripcion && doc.descripcion.toLowerCase().includes(busquedaLower))
      );
    }

    if (fechaDesde) {
      const fechaDesdeValue = new Date(fechaDesde);
      resultado = resultado.filter(doc => new Date(doc.fecha_subida || doc.fecha_creacion) >= fechaDesdeValue);
    }

    if (fechaHasta) {
      const fechaHastaValue = new Date(fechaHasta);
      fechaHastaValue.setHours(23, 59, 59, 999);
      resultado = resultado.filter(doc => new Date(doc.fecha_subida || doc.fecha_creacion) <= fechaHastaValue);
    }

    // Filtro por comunidad (solo admin)
    if (filtroComunidad) {
      resultado = resultado.filter(doc => String(doc.comunidad_id) === String(filtroComunidad));
    }

    setDocumentosFiltrados(resultado);
    setCurrentPage(1);
  };

  const cargarComunidades = async () => {
    try {
      const response = await getComunidades();
      setComunidades(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm });
  };
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  const handleOpenModal = () => setShowModal(true);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nuevoDoc.nombre || !nuevoDoc.archivo) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    setLoadingSave(true);
    try {
      const data = new FormData();
      data.append('nombre', nuevoDoc.nombre);
      data.append('descripcion', nuevoDoc.descripcion);
      data.append('categoria', nuevoDoc.categoria);
      if (user?.rol === 'administrador') {
        data.append('comunidad_id', nuevoDoc.comunidad_id);
      }
      data.append('archivo', nuevoDoc.archivo);

      await createDocumento(data);
      toast.success('Documento subido con éxito');
      setShowModal(false);
      setNuevoDoc({ nombre: '', descripcion: '', categoria: 'acta', comunidad_id: '', archivo: null });
      cargarDocumentos();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingSave(false);
    }
  };

  const handleBulkFileChange = (e) => {
    const files = Array.from(e.target.files).filter(f => {
      // Ignorar ficheros ocultos del sistema (._DS_Store, Thumbs.db, etc.)
      const name = f.name;
      return !name.startsWith('.') && name !== 'Thumbs.db' && name !== 'desktop.ini';
    });
    setBulkFiles(prev => {
      // Si ya hay archivos seleccionados, añadir los nuevos (sin duplicados por nombre)
      const existingNames = new Set(prev.map(f => f.archivo.name + f.archivo.size));
      const nuevos = files
        .filter(f => !existingNames.has(f.name + f.size))
        .map(f => ({
          archivo: f,
          nombre: f.name.replace(/\.[^/.]+$/, ''),
          categoria: 'acta',
        }));
      return [...prev, ...nuevos];
    });
    setBulkProgress(null);
    // Limpiar el input para permitir volver a seleccionar la misma carpeta
    e.target.value = '';
  };

  const updateBulkFile = (index, field, value) => {
    setBulkFiles(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
  };

  const removeBulkFile = (index) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleBulkSubmit = async () => {
    if (bulkFiles.length === 0) return;
    if (user?.rol === 'administrador' && !bulkComunidadId) {
      toast.error('Selecciona una comunidad para todos los documentos');
      return;
    }
    const total = bulkFiles.length;
    const errors = [];
    setBulkProgress({ current: 0, total, errors: [] });

    for (let i = 0; i < bulkFiles.length; i++) {
      const item = bulkFiles[i];
      try {
        // Validar tamaño antes de enviar (200MB)
        if (item.archivo.size > 209715200) {
          errors.push({ nombre: item.nombre, motivo: 'supera 200 MB' });
          setBulkProgress({ current: i + 1, total, errors: [...errors] });
          continue;
        }
        const data = new FormData();
        data.append('nombre', item.nombre);
        data.append('descripcion', '');
        data.append('categoria', item.categoria);
        if (user?.rol === 'administrador') data.append('comunidad_id', bulkComunidadId);
        data.append('archivo', item.archivo);
        await createDocumento(data);
      } catch (err) {
        const status = err.response?.status;
        const motivo = status === 413 ? 'archivo demasiado grande'
          : status === 400 ? (err.response?.data?.error || 'formato no permitido')
          : 'error del servidor';
        errors.push({ nombre: item.nombre, motivo });
      }
      setBulkProgress({ current: i + 1, total, errors: [...errors] });
    }

    const ok = total - errors.length;
    if (errors.length === 0) {
      toast.success(`${total} documento${total > 1 ? 's' : ''} subido${total > 1 ? 's' : ''} correctamente`);
    } else {
      toast.error(`${ok} subido${ok !== 1 ? 's' : ''}, ${errors.length} con error`);
    }
    cargarDocumentos();
    setTimeout(() => {
      if (errors.length === 0) {
        setShowBulkModal(false);
        setBulkFiles([]);
        setBulkComunidadId('');
        setBulkProgress(null);
      }
    }, 1500);
  };

  const handleEliminar = (id) => {
    openConfirm(
      'Eliminar documento',
      '¿Estás seguro de que quieres eliminar este documento? Esta acción no se puede deshacer.',
      () => {
        closeConfirm();
        deleteDocumento(id)
          .then(() => {
            toast.success('Documento eliminado con éxito');
            cargarDocumentos();
          })
          .catch(() => {
            toast.error('Error al eliminar documento');
          });
      }
    );
  };

  const handleDownload = (doc) => {
    try {
      const link = document.createElement('a');
      link.href = doc.archivo;
      link.download = doc.nombre || doc.archivo.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Descarga iniciada');
    } catch (error) {
      toast.error('Error al descargar documento');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const totalPages = Math.ceil(documentosFiltrados.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const documentosFiltradosPaginados = documentosFiltrados.slice(indexOfFirstItem, indexOfLastItem);
  const getCategoriaLabel = (categoria) => {
    return categorias[categoria]?.label || categoria;
  };

  const getFileMeta = (doc) => {
    const fileName = (doc.archivo || doc.nombre || '').toLowerCase();
    const ext = fileName.includes('.') ? fileName.split('.').pop() : '';
    if (['pdf'].includes(ext)) return { label: 'PDF', kind: 'pdf' };
    if (['doc', 'docx'].includes(ext)) return { label: 'WORD', kind: 'word' };
    if (['xls', 'xlsx', 'csv'].includes(ext)) return { label: 'EXCEL', kind: 'excel' };
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return { label: 'IMG', kind: 'image' };
    return { label: 'FILE', kind: 'file' };
  };

  const esNuevo = (fecha) => {
    if (!fecha) return false;
    const fechaDoc = new Date(fecha);
    if (Number.isNaN(fechaDoc.getTime())) return false;
    const diff = Date.now() - fechaDoc.getTime();
    return diff <= 7 * 24 * 60 * 60 * 1000;
  };

  const categoriasEnDatos = Array.from(new Set(documentos.map((doc) => doc.categoria).filter(Boolean)));
  const mostrarCategorias = categoriasEnDatos.length > 0;

  return (
    <div className="documentos-page">
      <header className="documentos-header">
        <div>
          <h1 className="documentos-title">📁 Documentos</h1>
          <p className="documentos-subtitle">Actas, normativa y archivos de la comunidad</p>
        </div>
        <div className="documentos-header-actions">
          <span className="documentos-count">{documentos.length} documentos</span>
          {user?.rol === 'administrador' && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="secondary" size="md" onClick={() => { setBulkFiles([]); setBulkProgress(null); setShowBulkModal(true); }}>
                Subida masiva
              </Button>
              <Button variant="primary" size="md" onClick={handleOpenModal}>
                Subir documento
              </Button>
            </div>
          )}
        </div>
      </header>

      <section className="documentos-toolbar">
        <div className="documentos-search">
          <span className="documentos-search-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </span>
          <input
            type="text"
            className="form-control documentos-search-input"
            placeholder="Buscar documento..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div className="documentos-filter-row">
          <div className="documentos-filter">
            <label className="form-label">Tipo</label>
            <select
              className="form-control"
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
            >
              <option value="">Todos</option>
              {Object.entries(categorias).map(([key, cat]) => (
                <option key={key} value={key}>{cat.icon} {cat.label}</option>
              ))}
            </select>
          </div>
          <button
            className="documentos-advanced-toggle"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            {showAdvancedFilters ? 'Basico' : 'Mas filtros'}
          </button>
        </div>

        {showAdvancedFilters && (
          <div className="documentos-advanced-filters">
            <div className="documentos-filter-dates">
              <div className="documentos-filter">
                <label className="form-label">Desde</label>
                <DateInput
                  value={fechaDesde}
                  onChange={(val) => setFechaDesde(val)}
                  placeholder="dd-mm-aaaa"
                />
              </div>
              <div className="documentos-filter">
                <label className="form-label">Hasta</label>
                <DateInput
                  value={fechaHasta}
                  onChange={(val) => setFechaHasta(val)}
                  placeholder="dd-mm-aaaa"
                />
              </div>
              {user?.rol === 'administrador' && comunidades.length > 0 && (
                <div className="documentos-filter">
                  <label className="form-label">Comunidad</label>
                  <select
                    className="form-control"
                    value={filtroComunidad}
                    onChange={(e) => setFiltroComunidad(e.target.value)}
                  >
                    <option value="">Todas</option>
                    {comunidades.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {loading ? (
        <div className="documentos-skeleton-list">
          {[1, 2, 3, 4, 5].map((item) => (
            <div key={item} className="documentos-skeleton"></div>
          ))}
        </div>
      ) : documentosFiltrados.length === 0 ? (
        <EmptyState
          icon="📂"
          title={filtroCategoria ? 'No hay documentos en esta categoría' : 'No hay documentos disponibles'}
          message={filtroCategoria 
            ? 'Intenta seleccionar otra categoría o quitar los filtros'
            : 'Cuando se suban documentos aparecerán aquí'}
          action={
            user?.rol === 'administrador' && (
              <Button variant="primary" size="md" onClick={handleOpenModal}>
                + Subir Documento
              </Button>
            )
          }
        />
      ) : (
        <>
          <div className="documentos-feed">
            {documentosFiltradosPaginados.map((doc) => {
              const fileMeta = getFileMeta(doc);
              const isNew = esNuevo(doc.fecha_subida || doc.fecha_creacion);
              return (
                <article
                  key={doc.id}
                  className="documentos-card"
                  onClick={() => handleDownload(doc)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleDownload(doc);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="documentos-card-header">
                    <div className="documentos-card-title-group">
                      <div className={`documentos-file-icon documentos-file-icon--${fileMeta.kind}`}>
                        {fileMeta.label}
                      </div>
                      <h3 className="documentos-card-title">{doc.nombre}</h3>
                      {isNew && <span className="documentos-new">Nuevo</span>}
                    </div>
                    {doc.categoria && (
                      <span className="documentos-tag">{getCategoriaLabel(doc.categoria)}</span>
                    )}
                  </div>

                  {doc.descripcion && (
                    <p className="documentos-card-description">{doc.descripcion}</p>
                  )}

                  <div className="documentos-card-footer">
                    <div className="documentos-card-meta">
                      {(doc.fecha_subida || doc.fecha_creacion) && (
                        <span className="documentos-card-meta-item">
                          {new Date(doc.fecha_subida || doc.fecha_creacion).toLocaleDateString('es-ES')}
                        </span>
                      )}
                      {doc.tamano && (
                        <span className="documentos-card-meta-item">{formatBytes(doc.tamano)}</span>
                      )}
                    </div>
                    <div className="documentos-card-actions" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDownload(doc)}
                      >
                        Descargar
                      </Button>
                      {user?.rol === 'administrador' && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEliminar(doc.id);
                          }}
                        >
                          Eliminar
                        </Button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={documentosFiltrados.length}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </>
      )}

      {/* Modal para subir documento */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(8px)'
        }} onClick={() => setShowModal(false)}>
          <div
            style={{
              background: '#ffffff',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--space-6)',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
              <h2 style={{ fontSize: 'var(--font-2xl)', fontWeight: '700', margin: 0 }}>Subir Documento</h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer'
                }}
              >
                ✖️
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Nombre del Documento</label>
                <input
                  type="text"
                  className="form-control"
                  value={nuevoDoc.nombre}
                  onChange={(e) => setNuevoDoc({ ...nuevoDoc, nombre: e.target.value })}
                  required
                />
              </div>

              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Descripción (opcional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={nuevoDoc.descripcion}
                  onChange={(e) => setNuevoDoc({ ...nuevoDoc, descripcion: e.target.value })}
                />
              </div>

              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Categoría</label>
                <select
                  className="form-control"
                  value={nuevoDoc.categoria}
                  onChange={(e) => setNuevoDoc({ ...nuevoDoc, categoria: e.target.value })}
                  required
                >
                  <option value="">Seleccionar categoría</option>
                  {Object.entries(categorias).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.icon} {cat.label}</option>
                  ))}
                </select>
              </div>

              {(user.rol === 'administrador') && comunidades.length > 0 && (
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <label className="form-label">Comunidad</label>
                  <select
                    className="form-control"
                    value={nuevoDoc.comunidad_id}
                    onChange={(e) => setNuevoDoc({ ...nuevoDoc, comunidad_id: e.target.value })}
                    required
                  >
                    <option value="">— Seleccionar comunidad —</option>
                    {comunidades.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Archivo</label>
                <div style={{
                  border: '2px dashed var(--primary)',
                  borderRadius: 'var(--radius-button)',
                  padding: 'var(--space-4)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background: nuevoDoc.archivo ? 'var(--bg-light)' : 'transparent'
                }}>
                  <input
                    type="file"
                    onChange={(e) => setNuevoDoc({ ...nuevoDoc, archivo: e.target.files[0] })}
                    style={{ display: 'none' }}
                    id="file-input"
                  />
                  <label htmlFor="file-input" style={{ cursor: 'pointer', display: 'block' }}>
                    <span style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }}>📄</span>
                    <span style={{ fontWeight: '600' }}>
                      {nuevoDoc.archivo ? nuevoDoc.archivo.name : 'Haz clic para seleccionar o arrastra un archivo'}
                    </span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loadingSave || !nuevoDoc.nombre || !nuevoDoc.archivo || (user.rol === 'administrador' && !nuevoDoc.comunidad_id)}
                  style={{ flex: 1 }}
                >
                  {loadingSave ? 'Subiendo...' : 'Subir'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowModal(false)}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal subida masiva */}
      {showBulkModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(8px)' }}
          onClick={() => !bulkProgress && setShowBulkModal(false)}>
          <div style={{ background: '#fff', borderRadius: 'var(--radius-xl)', padding: 'var(--space-6)', width: '90%', maxWidth: '620px', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
              <h2 style={{ fontSize: 'var(--font-2xl)', fontWeight: '700', margin: 0 }}>Subida masiva</h2>
              {!bulkProgress && (
                <button onClick={() => setShowBulkModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>✖️</button>
              )}
            </div>

            {/* Selector comunidad (admin) */}
            {user?.rol === 'administrador' && comunidades.length > 0 && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Comunidad (para todos los documentos)</label>
                <select className="form-control" value={bulkComunidadId} onChange={e => setBulkComunidadId(e.target.value)} required disabled={!!bulkProgress}>
                  <option value="">— Seleccionar comunidad —</option>
                  {comunidades.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
            )}

            {/* Drop zone */}
            {!bulkProgress && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <input type="file" multiple id="bulk-file-input" style={{ display: 'none' }} onChange={handleBulkFileChange} />
                <label htmlFor="bulk-file-input" style={{ cursor: 'pointer', border: '2px dashed var(--primary)', borderRadius: 'var(--radius-button)', padding: 'var(--space-5)', textAlign: 'center', display: 'block' }}>
                  <span style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }}>📂</span>
                  <span style={{ fontWeight: '600', display: 'block' }}>Haz clic para seleccionar archivos</span>
                  <span style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                    Navega hasta la carpeta que quieras y selecciona los archivos con <strong>Ctrl+clic</strong>
                  </span>
                  <span style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>PDF, Word, Excel, ZIP, imágenes — hasta 200 MB por archivo</span>
                </label>
              </div>
            )}

            {/* Lista de ficheros */}
            {bulkFiles.length > 0 && !bulkProgress && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>{bulkFiles.length} archivo{bulkFiles.length > 1 ? 's' : ''} seleccionado{bulkFiles.length > 1 ? 's' : ''}:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '280px', overflowY: 'auto' }}>
                  {bulkFiles.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', background: '#f8fafc', borderRadius: '8px', padding: '8px 10px' }}>
                      <span style={{ fontSize: '18px' }}>📄</span>
                      <input
                        className="form-control"
                        style={{ flex: 2, fontSize: '13px', padding: '4px 8px' }}
                        value={item.nombre}
                        onChange={e => updateBulkFile(idx, 'nombre', e.target.value)}
                        placeholder="Nombre del documento"
                      />
                      <select
                        className="form-control"
                        style={{ flex: 1, fontSize: '13px', padding: '4px 8px' }}
                        value={item.categoria}
                        onChange={e => updateBulkFile(idx, 'categoria', e.target.value)}
                      >
                        {Object.entries(categorias).map(([key, cat]) => (
                          <option key={key} value={key}>{cat.label}</option>
                        ))}
                      </select>
                      <button onClick={() => removeBulkFile(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '16px', padding: '0 4px' }}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Barra de progreso */}
            {bulkProgress && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>
                  Subiendo... {bulkProgress.current} / {bulkProgress.total}
                </p>
                <div style={{ background: '#e5e7eb', borderRadius: '99px', height: '10px', overflow: 'hidden' }}>
                  <div style={{ background: 'var(--primary)', height: '100%', borderRadius: '99px', width: `${(bulkProgress.current / bulkProgress.total) * 100}%`, transition: 'width 0.3s ease' }} />
                </div>
                {bulkProgress.errors.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <p style={{ color: '#ef4444', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                      Archivos con error:
                    </p>
                    {bulkProgress.errors.map((e, i) => (
                      <p key={i} style={{ color: '#ef4444', fontSize: '12px', margin: '2px 0' }}>
                        ✕ <strong>{e.nombre}</strong> — {e.motivo}
                      </p>
                    ))}
                  </div>
                )}
                {bulkProgress.current === bulkProgress.total && (
                  <p style={{ color: '#22c55e', fontWeight: '600', marginTop: '8px' }}>
                    ✅ Completado
                  </p>
                )}
              </div>
            )}

            {/* Botones */}
            {!bulkProgress && (
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <Button
                  variant="primary"
                  style={{ flex: 1 }}
                  disabled={bulkFiles.length === 0 || (user?.rol === 'administrador' && !bulkComunidadId)}
                  onClick={handleBulkSubmit}
                >
                  Subir {bulkFiles.length > 0 ? `${bulkFiles.length} archivo${bulkFiles.length > 1 ? 's' : ''}` : 'archivos'}
                </Button>
                <Button variant="ghost" style={{ flex: 1 }} onClick={() => setShowBulkModal(false)}>Cancelar</Button>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        confirmLabel="Eliminar"
        variant="danger"
      />
    </div>
  );
}

export default Documentos;
